import {ApiRouteConfig} from '../../../config/api.route.config';
import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {ActiveSubscription} from '../../subscription/model/active-subscription';
import {ActiveSubscriptionProduct} from '../../subscription/model/active-subscription-product';
import {TransactionHistoryEntry} from '../../subscription/model/transaction-history-entry';
import * as FileSaver from 'file-saver';
import {forkJoin, Subject, of} from 'rxjs';
import {map, catchError} from 'rxjs/operators';
import {BuildingProductType} from '../enums/enums';
import { TokenService } from './token.service';
import { PaymentProduct } from '../../payment/model/payment-product';
import { PaymentOverview } from '../../payment/model/payment-overview';
import { camelCase } from 'lodash';

@Injectable()
export class SubscriptionService {
  public $subscriptionChanged = new Subject();

  constructor(private httpClient: HttpClient, private config: ApiRouteConfig,
              private tokenService: TokenService) {}

  public isUserPremium(): boolean {
    return this.tokenService.getBusinessProducts().includes(BuildingProductType.PREMIUMSUBSCRIPTION);
  }

  public isUserLite(): boolean {
    return this.tokenService.getBusinessProducts().includes(BuildingProductType.LITESUBSCRIPTION);
  }

  public isUserOnDemo(): boolean {
    return this.tokenService.getBusinessProducts().includes(BuildingProductType.DEMOSUBSCRIPTION);
  }

  public getDemoExpirationDate(): Observable<string> {
    return this.httpClient.get<any[]>(this.config.BUSINESS_PRODUCTS).pipe(
      map(products => products.find(product => product.productType === BuildingProductType.DEMOSUBSCRIPTION).expirationDate),
      catchError(error => {
        return of('');
      })
    );
  }

  public getActiveSubscriptionInfo(): Observable<ActiveSubscription> {
    const first = this.httpClient.get<any>(this.config.PAYMENT_SUBSCRIPTION_SUMMARY);
    const second = this.httpClient.get<any>(this.config.RECURRING_PAYMENT_METHOD_ACTIVE);
    return forkJoin(first, second).pipe(map(res => {
      return this.createActiveSubscription(res);
    }));
  }

  public deactivateSubscription(): Observable<any> {
    return this.httpClient.get<any>(this.config.RECURRING_PAYMENT_METHOD_DEACTIVATE);
  }

  public getTransactionHistory(): Observable<TransactionHistoryEntry[]> {
    return this.httpClient.get<TransactionHistoryEntry[]>(this.config.PAYMENT_COMPLETED_TRANSACTION_HISTORY);
  }

  public downloadInvoice(id: number): Observable<boolean> {
    return this.httpClient.get(
      this.config.getRouteWithInjectedParams(this.config.PAYMENT_DOWNLOAD_ORDER_INVOICE, [id.toString()]), {observe: 'response', responseType: 'arraybuffer'})
      .pipe(map((response) => {
          const blob = new Blob([response.body], {type: response.headers.get('content-type')});
          FileSaver.saveAs(blob, response.headers.get('filename'));
          return true;
        },
        err => {
          return false;
        }));
  }

  private createActiveSubscription(res: any): ActiveSubscription {
    const products = this.createProductsFromSubscriptionSummary(res[0]);
    const summary = res[0];
    const active = res[1];
    const status = (summary == null && active == null) ? 'inactive' : (active == null) ? 'cancelled' : 'active';
    let result: any = {};
    switch (status) {
      case 'active':
        result = {
          subscriptionProductType: summary.subscriptionProductType,
          nextSubscriptionFee: summary.nextPaymentDate,
          cardBrand: active.cardBrand || '',
          maskedCardNumber: active.maskedCardNumber || '',
          cardExpirationDate: active.cardExpirationDate || '',
          status: status,
        };
        result.products = products;
        break;
      case 'cancelled':
        result = {
          subscriptionProductType: summary.subscriptionProductType,
          nextSubscriptionFee: summary.nextPaymentDate,
          cardBrand: '',
          maskedCardNumber: '',
          cardExpirationDate: '',
          status: status,
        };
        result.products = products;
        break;
      case 'inactive':
        result = {products: []};
        break;
    }
    return result;
  }

  private createProductsFromSubscriptionSummary(summary: any): ActiveSubscriptionProduct[] {
    let result = [];
    if (summary != null && summary.nextPaymentOverviewModel != null) {

      let overview: PaymentOverview = summary.nextPaymentOverviewModel;
      var subscription: BuildingProductType = summary.subscriptionProductType;

      let buildingProduct = this.getSubscriptionPaymentProduct(subscription, overview, BuildingProductType.BUILDINGS);
      let userProduct = this.getSubscriptionPaymentProduct(subscription, overview, BuildingProductType.USERS);
      let inspectionProduct = this.getSubscriptionPaymentProduct(subscription, overview, BuildingProductType.INSPECTIONS);

      if (buildingProduct) result.push(this.getActiveSubscriptionProductForProduct(buildingProduct, overview.currency, summary.previousNumberOfBuildings, overview.detectedNumberOfBuildings));
      if (userProduct) result.push(this.getActiveSubscriptionProductForProduct(userProduct, overview.currency, summary.previousNumberOfUsers, overview.detectedNumberOfUsers));
      if (inspectionProduct) result.push(this.getActiveSubscriptionProductForProduct(inspectionProduct, overview.currency, summary.previousNumberOfInspections, overview.detectedNumberOfInspections));
    }
    return result;
  }

  public getSubscriptionPaymentProduct(subscription: BuildingProductType, paymentOverview: PaymentOverview, product: BuildingProductType): PaymentProduct {
    var subscriptionProducts = this.getSubscriptionProducts(subscription, paymentOverview);

    if (subscriptionProducts[camelCase(product)])
    {
      return subscriptionProducts[camelCase(product)];
    }
    return null;
  }

  public getSubscriptionProducts(subscription: BuildingProductType, paymentOverview: PaymentOverview): Map<BuildingProductType, PaymentProduct>  {
    return paymentOverview.subscriptionCatalogue[camelCase(subscription)];
  }

  private getActiveSubscriptionProductForProduct(product: any, currency: string, previousQuantity: number, nextQuantity: number): ActiveSubscriptionProduct {
    return {
      label: product.label,
      price: product.price,
      discountPrice: product.discountPrice,
      recurrency: product.recurrency,
      currency: currency,
      lastQuantity: previousQuantity,
      nextQuantity: nextQuantity
    } as ActiveSubscriptionProduct;
  }
}
